// The two lines app#170 adds under the meters: when the allowance comes back, and how old the
// number above it is.

// Axes that RESET. Bandwidth and compute are flows, measured over the period and back to zero on
// the 1st.
//
// ⚠ Storage and nodes are LEVELS, not flows: storage is the period's peak and nodes is a count.
// Neither "resets", and printing that they do would promise a customer their stored bytes go away
// on the 1st.
const RESETS = new Set(['bandwidth', 'compute'])

export const resetsOn = (axis: string): boolean => RESETS.has(axis)

// "1 October (UTC)" — the ONE rendering of a period boundary, shared by every surface that prints
// one, and it NAMES THE ZONE.
//
// ⚠⚠ RENDERED IN UTC, AND THAT IS NOT A STYLE CHOICE. The billing period is a UTC calendar month
// — `currentPeriod()` slices an ISO string and the server's PeriodBounds agrees — so the boundary
// instant is midnight UTC on the 1st. Formatted in the reader's zone, everyone west of Greenwich
// reads "31 Aug" for a period that ends on the 1st, which is the wrong DAY on a billing screen. A
// boundary the server defines is shown in the server's zone.
//
// ⚠⚠ AND THE ZONE IS PRINTED, WHICH IS app#208. Rendering the right instant is not enough on its
// own: `current_period_end` of `2026-10-01 00:00:00+00` is 30 September in US Pacific, so Stripe's
// portal said "your next billing date is September 30, 2026" one click from a page that said
// 1 October. Both are right and the customer cannot tell that, so the zone goes on the line.
//
// ⚠⚠ THE YEAR IS PRINTED ONLY WHEN IT IS NOT THE CURRENT ONE, AND THAT IS app#225. An ANNUAL
// subscription ending `2027-10-01` was rendered "renews October 1 (UTC)" to a reader in 2026,
// which reads as a date eleven months PAST — on the absolute half that app#208 added precisely so
// a customer could check it against Stripe's portal. An unconditional year would fix that and make
// every monthly usage ring noisier for no gain, since those boundaries are weeks away. One
// formatter, one rule inside it, so the surfaces still cannot drift apart.
//
// ⚠⚠ THE COMPARISON IS IN UTC ON BOTH SIDES. Read the current year in the reader's zone and the
// label disagrees with itself over New Year: a reader west of Greenwich on 31 December 2026 local
// is already in 2027 UTC, so a 2027 boundary would gain a year the day before it stopped needing
// one, and a 2026 one would lose it.
//
// ⚠ Returns '' for an unparseable stamp, so a caller prints nothing rather than "Invalid Date".
export const periodDateUTC = (periodEndISO: string, now: number = Date.now()): string => {
  const d = new Date(periodEndISO)
  if (Number.isNaN(d.getTime())) return ''
  const sameYear = d.getUTCFullYear() === new Date(now).getUTCFullYear()
  // ⚠ `undefined` locale, so the day/month ORDER follows the reader while the ZONE does not. The
  // two are different questions: which day this is, is the server's to say; how a date is written
  // is the reader's.
  const date = d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  })
  return `${date} (UTC)`
}

/**
 * A single INSTANT, for a row that records something that happened at a moment (app#256).
 *
 * ⚠⚠ IT IS NOT `periodDateUTC` AND MUST NOT BE FOLDED INTO IT. That one answers a BOUNDARY — the
 * renewal date, the allowance reset — where a time of day would be noise, and three surfaces share
 * it so they cannot drift (app#208). This answers WHEN SOMETHING HAPPENED, and dropping the time
 * makes two events at different moments render identically.
 *
 * ⚠⚠ THAT IS EXACTLY WHAT WENT WRONG. The purchase list rendered `periodDateUTC` and produced
 * three byte-identical rows — "September 3 (UTC) · $20.00 · Paid · automatic" — on a list whose
 * only job is letting a customer reconcile a card statement against a specific charge
 * (nobgp-router#1209). A statement with three $20.00 charges mapped onto three rows nobody could
 * tell apart.
 *
 * ⚠ THE ZONE STAYS. app#208 established that a date without its zone cannot be checked against
 * Stripe, and this list is read beside a statement rendered in the customer's own timezone.
 *
 * ⚠ NO SECONDS. Two purchases inside one minute are possible and are separated by the amount and
 * the kind beside them; seconds would widen the column for a case that costs nothing today.
 *
 * ⚠ The YEAR follows the same rule as `periodDateUTC` — shown only when it is not the current one
 * — so a purchase from a past year cannot read as a recent one.
 *
 * ⚠ Returns '' for an unparseable stamp, so a caller prints nothing rather than "Invalid Date".
 */
export const instantUTC = (iso: string, now: number = Date.now()): string => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const sameYear = d.getUTCFullYear() === new Date(now).getUTCFullYear()
  const date = d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  })
  const time = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
  })
  return `${date}, ${time} UTC`
}

// The same instant, from Stripe's Unix-seconds stamp (invoice rows, app#267).
//
// ⚠ A non-finite `seconds` returns '' here. `new Date(NaN).toISOString()` THROWS `RangeError`,
// and the app has no ErrorBoundary, so one bad `created` on one invoice row would unmount the
// whole page. `instantUTC` already returns '' for a stamp it cannot parse; this keeps that rule.
export const instantUTCFromUnixSeconds = (seconds: number, now: number = Date.now()): string =>
  Number.isFinite(seconds) ? instantUTC(new Date(seconds * 1000).toISOString(), now) : ''

// "Resets 1 October (UTC)" — from the period the screen is showing, which is the same boundary the
// app already sends to get_org_usage as `p_end`.
//
// ⚠⚠ A PAST PERIOD GETS NOTHING. July's allowance did not reset on 1 August in any sense a reader
// would take from that word — it ended, and the page has a six-month picker one click away.
//
// ⚠ The date itself comes from `periodDateUTC` rather than being formatted here, because at least
// three surfaces print this instant — this label, the usage rings that also call it, and the
// billing period line through `periodDateLabel` — and they agreed only by coincidence (app#208).
export const resetLabel = (period: string, currentPeriod: string, periodEndISO: string): string => {
  if (period !== currentPeriod) return ''
  const date = periodDateUTC(periodEndISO)
  if (!date) return ''
  return `Resets ${date}`
}

// How old the number is, in the reader's words.
//
// ⚠⚠ NEVER "just now" ON A GUESS. The stamp is the newest measurement the figure includes, read
// from the data; if it is absent we say nothing at all rather than implying the screen is live.
// Our sums lag their rollups, which is exactly why an honest stamp is worth printing.
//
// ⚠ Under a minute reads as "moments ago" rather than "0 minutes ago", and anything older than a
// day gets the date: "2 days ago" on a billing screen invites arithmetic the reader should not
// have to do.
export const freshnessLabel = (asOf: string | null | undefined, now: number = Date.now()): string => {
  if (!asOf) return ''
  const t = new Date(asOf).getTime()
  if (Number.isNaN(t)) return ''
  const minutes = Math.floor((now - t) / 60_000)
  // ⚠ A stamp in the FUTURE is a clock difference between this browser and the database, not a
  // measurement from the future. Read it as current rather than printing a negative age.
  if (minutes <= 0) return 'Updated moments ago'
  if (minutes === 1) return 'Updated 1 minute ago'
  if (minutes < 60) return `Updated ${minutes} minutes ago`
  const hours = Math.floor(minutes / 60)
  if (hours === 1) return 'Updated 1 hour ago'
  if (hours < 24) return `Updated ${hours} hours ago`
  // ⚠ The reader's OWN zone here, unlike the reset date above, and the difference is deliberate:
  // this is an INSTANT something happened, so "when was that for me" is the question. A boundary
  // the server defines is a different kind of fact and is shown in UTC.
  return `Updated ${new Date(asOf).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`
}
